import { Box } from "@mui/material";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { clampNumber } from "@entrilia/shared/utilities/numberHelper";

const initialRightWidth = 440;
const minLeftWidth = 480;
const minRightWidth = 360;
const dividerHitboxWidth = 8;
const dividerHandleWidth = 4;
const dividerHandleHeight = 36;

interface Props {
  leftPane: ReactNode;
  rightPane: ReactNode;
  isRightPaneCollapsed: boolean;
}

const ArtifactSplitPane = ({ leftPane, rightPane, isRightPaneCollapsed }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rightPaneRef = useRef<HTMLDivElement | null>(null);
  const dragStartXRef = useRef(0);
  const dragStartRightWidthRef = useRef(0);
  const [rightWidth, setRightWidth] = useState(initialRightWidth);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isDragging]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartXRef.current = event.clientX;
    dragStartRightWidthRef.current = rightPaneRef.current?.getBoundingClientRect().width ?? rightWidth;
    setIsDragging(true);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }

    const containerWidth = containerRef.current?.getBoundingClientRect().width;
    if (containerWidth === undefined) {
      return;
    }

    const requestedWidth = dragStartRightWidthRef.current - (event.clientX - dragStartXRef.current);
    const maxRightWidth = Math.max(minRightWidth, containerWidth - minLeftWidth - dividerHitboxWidth);
    setRightWidth(clampNumber(requestedWidth, minRightWidth, maxRightWidth));
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);
  };

  return (
    <Box ref={containerRef} sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "auto" }}>{leftPane}</Box>
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onLostPointerCapture={() => setIsDragging(false)}
        sx={{
          display: "flex",
          flex: `0 0 ${dividerHitboxWidth}px`,
          alignItems: "center",
          justifyContent: "center",
          cursor: "col-resize",
          pointerEvents: isRightPaneCollapsed ? "none" : "auto",
          userSelect: "none",
          touchAction: "none",
          "&::before": {
            content: '""',
            display: isRightPaneCollapsed ? "none" : "block",
            width: dividerHandleWidth,
            height: dividerHandleHeight,
            borderRadius: `${dividerHandleWidth / 2}px`,
            bgcolor: isDragging ? "action.active" : "divider",
          },
          "&:hover::before": {
            bgcolor: "action.active",
          },
        }}
      />
      <Box
        ref={rightPaneRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: isRightPaneCollapsed ? "0 0 auto" : `0 0 ${rightWidth}px`,
          maxWidth: isRightPaneCollapsed
            ? "none"
            : `max(${minRightWidth}px, calc(100% - ${minLeftWidth + dividerHitboxWidth}px))`,
          minWidth: 0,
        }}
      >
        {rightPane}
      </Box>
    </Box>
  );
};

export default ArtifactSplitPane;
